import { QueryClient, useQuery } from 'react-query';

import { GqlRequestOptions } from '@common/api-request';

import { getPdpBaseInfo } from '../graphql/pdp-base-info.2';
import {
  GetPdpBaseInfo_catalog_product_base_info_brand_home,
  GetPdpBaseInfo_catalog_product_base_info_flags,
  GetPdpBaseInfo_catalog_product_base_info_key_color,
  GetPdpBaseInfo_catalog_product_base_info_policy_list,
  GetPdpBaseInfo_catalog_product_base_info_product,
  GetPdpBaseInfo_catalog_product_base_info_seller_notice,
  GetPdpBaseInfo_catalog_product_base_info_shop,
  GetPdpBaseInfo_pdp_btf_page_tab_list,
  PdpBrowsingType,
  PdpCatalogDisplayStatus,
  PdpCatalogFulfillmentType,
  PdpCatalogPaymentType,
  PdpCatalogSalesStatus,
  PdpCouponAvailableStatus,
  PdpCouponDiscountType,
  PdpProductEntryType,
  PdpShopType,
} from '../graphql/pdp-base-info_types.2';
import { queryOptions } from '../lib/queryHelpers';
import { checkValidCatalogProductId } from '../lib/utils';

const ERROR_INVALID_PARAMS_CATALOG_PRODUCT_ID = new Error('invalid params { catalog_product_id }');
const ERROR_FAILED_FETCH = new Error('failed to fetch');

interface PdpBaseInfo {
  brand_home: GetPdpBaseInfo_catalog_product_base_info_brand_home | null;
  product: GetPdpBaseInfo_catalog_product_base_info_product;
  recommendation_tab_label: string;
  shop: GetPdpBaseInfo_catalog_product_base_info_shop;
  flags: GetPdpBaseInfo_catalog_product_base_info_flags;
  policy_list: GetPdpBaseInfo_catalog_product_base_info_policy_list[];
  seller_notice: GetPdpBaseInfo_catalog_product_base_info_seller_notice | null;
  key_color: GetPdpBaseInfo_catalog_product_base_info_key_color;
  tab_list: GetPdpBaseInfo_pdp_btf_page_tab_list[] | null;
}

const toData = (data?: PdpBaseInfo) => {
  return {
    product: {
      id: data?.product.id || '',
      name: data?.product.name || '',
      product_url: data?.product.product_url || '',
      is_saved_product: data?.product.is_saved_product ?? false,
      top_summary_notice_text: data?.product.top_summary_notice_text || null,
      source_product_url: data?.product.source_product_url || null,
      tracking_product_url: data?.product.tracking_product_url || null,
      external_product_url: data?.product.external_product_url || null,
      fulfillment_type: data?.product.fulfillment_type as PdpCatalogFulfillmentType,
      entry_type: data?.product.entry_type as PdpProductEntryType,
      payment_type: data?.product.payment_type as PdpCatalogPaymentType,
      shop_id: data?.product.shop_id || 0,
      shop_main_domain: data?.product.shop_main_domain || '',
      external_code: data?.product.external_code || null,
      preparation_period: data?.product.preparation_period || null,
      sales_status: data?.product.sales_status as PdpCatalogSalesStatus,
      display_status: data?.product.display_status as PdpCatalogDisplayStatus,
      is_brand: data?.product.is_brand ?? false,
      category_key: data?.product.category_key || null,
      category_list: data?.product.category_list || null,
      product_image_list: data?.product.product_image_list || [],
      estimated_shipping_date: data?.product.estimated_shipping_date || null,
      managed_category_list: data?.product.managed_category_list || null,
      is_purchasable: data?.product.is_purchasable || true,
      gaugebar_coupon_not_applicable_text: data?.product.gaugebar_coupon_not_applicable_text || null,
      color_swatch_carousel: data?.product.color_swatch_carousel || null,
      product_price: {
        is_visible_discount_detail_button: data?.product.product_price.is_visible_discount_detail_button ?? false,
        first_order_discount: {
          promotion_id: data?.product.product_price.first_order_discount?.promotion_id ?? '',
          price: data?.product.product_price.first_order_discount?.price ?? 0,
          discount_amount: data?.product.product_price.first_order_discount?.discount_amount ?? 0,
          min_required_amount: data?.product.product_price.first_order_discount?.min_required_amount ?? 0,
          guidance_image_url: data?.product.product_price.first_order_discount?.guidance_image_url ?? null,
          discount_type:
            data?.product.product_price.first_order_discount?.discount_type || PdpCouponDiscountType.AMOUNT,
          discount_rate_bp: data?.product.product_price.first_order_discount?.discount_rate_bp ?? 0,
          discount_amount_of_amount_coupon:
            data?.product.product_price.first_order_discount?.discount_amount_of_amount_coupon ?? 0,
        },
        store_discount_info: {
          discount_price: data?.product.product_price.store_discount_info?.discount_price ?? 0,
          discount_amount: data?.product.product_price.store_discount_info?.discount_amount ?? 0,
          discount_rate: data?.product.product_price.store_discount_info?.discount_rate ?? 0,
        },
        final_discount_info: {
          discount_price: data?.product.product_price.final_discount_info.discount_price ?? 0,
          discount_amount: data?.product.product_price.final_discount_info.discount_amount ?? 0,
          discount_rate: data?.product.product_price.final_discount_info.discount_rate ?? 0,
        },
        product_promotion_discount_info: {
          discount_price: data?.product.product_price.product_promotion_discount_info?.discount_price ?? 0,
          discount_amount: data?.product.product_price.product_promotion_discount_info?.discount_amount ?? 0,
          discount_rate: data?.product.product_price.product_promotion_discount_info?.discount_rate ?? 0,
        },
        coupon_button_color: {
          normal: data?.product.product_price.coupon_button_color?.normal ?? '#FA6EE3',
        },
        coupon_discount_info: {
          title: data?.product.product_price.coupon_discount_info?.title ?? null,
          discount_price: data?.product.product_price.coupon_discount_info?.discount_price ?? 0,
          discount_amount: data?.product.product_price.coupon_discount_info?.discount_amount ?? 0,
          discount_type: data?.product.product_price.coupon_discount_info?.discount_type as PdpCouponDiscountType,
          discount_rate_bp: data?.product.product_price.coupon_discount_info?.discount_rate_bp ?? 0,
          display_discount_rate_bp: data?.product.product_price.coupon_discount_info?.display_discount_rate_bp ?? 0,
          discount_amount_of_amount_coupon:
            data?.product.product_price.coupon_discount_info?.discount_amount_of_amount_coupon ?? 0,
          min_required_amount: data?.product.product_price.coupon_discount_info?.min_required_amount ?? 0,
          max_discount_amount: data?.product.product_price.coupon_discount_info?.max_discount_amount ?? 0,
          target_type: data?.product.product_price.coupon_discount_info?.target_type ?? null,
        },
        coupon_discount_info_list: data?.product.product_price.coupon_discount_info_list || [],
        bogo_promotion_discount_info: {
          discount_price: data?.product.product_price.bogo_promotion_discount_info?.discount_price ?? 0,
          discount_amount: data?.product.product_price.bogo_promotion_discount_info?.discount_amount ?? 0,
          discount_rate: data?.product.product_price.bogo_promotion_discount_info?.discount_rate ?? 0,
        },
        max_price_info: {
          price: data?.product.product_price.max_price_info.price ?? 0,
          color: {
            normal: data?.product.product_price.max_price_info.color.normal ?? null,
          },
          badge: {
            text: data?.product.product_price.max_price_info.badge?.text ?? '',
            color: {
              normal: data?.product.product_price.max_price_info.badge?.color?.normal ?? null,
            },
          },
        },
        display_final_price: {
          discount_rate: data?.product.product_price.display_final_price.discount_rate ?? 0,
          discount_rate_color: {
            normal: data?.product.product_price.display_final_price.discount_rate_color.normal ?? null,
          },
          final_price: {
            price: data?.product.product_price.display_final_price.final_price.price ?? 0,
            price_with_currency: {
              display_currency:
                data?.product.product_price.display_final_price.final_price.price_with_currency.display_currency ||
                '원',
            },
            color: {
              normal: data?.product.product_price.display_final_price.final_price.color.normal ?? null,
            },
            badge: {
              text: data?.product.product_price.display_final_price.final_price?.badge?.text ?? '',
              color: {
                normal: data?.product.product_price.display_final_price.final_price?.badge?.color?.normal ?? null,
              },
            },
          },
          final_price_additional: {
            price: data?.product.product_price.display_final_price.final_price_additional?.price ?? 0,
            price_with_currency: {
              display_currency:
                data?.product.product_price.display_final_price.final_price_additional?.price_with_currency
                  .display_currency || '원',
            },
            color: {
              normal: data?.product.product_price.display_final_price.final_price_additional?.color.normal ?? null,
            },
            badge: {
              text: data?.product.product_price.display_final_price.final_price_additional?.badge?.text ?? '',
              color: {
                normal:
                  data?.product.product_price.display_final_price.final_price_additional?.badge?.color?.normal ?? null,
              },
            },
          },
        },
      },
      promotion_info: data?.product.promotion_info ?? null,
      promotion_message: data?.product.promotion_message || '',
      discount_info: data?.product.discount_info || null,
      coupon_available_status: data?.product.coupon_available_status as PdpCouponAvailableStatus,
      parallel_imported_banner_image_url: data?.product.parallel_imported_banner_image_url || null,
      minimum_order_quantity: data?.product.minimum_order_quantity || 0,
      maximum_order_quantity: data?.product.maximum_order_quantity || null,
      minimum_order_quantity_type: data?.product.minimum_order_quantity_type || null,
      pdp_image_rolling_banner: data?.product.pdp_image_rolling_banner || null,
      is_language_match: data?.product.is_language_match ?? false,
      preorder: data?.product.preorder || null,
      crema_fit_url_list: data?.product.crema_fit_url_list || [],
      product_item_attribute_list: data?.product.product_item_attribute_list || [],
      essentials: data?.product.essentials || [],
      trait_list: data?.product.trait_list || null,
      shipping_send_date: data?.product.shipping_send_date || null,
      is_sample: data?.product.is_sample ?? false,
      shipping_fee: data?.product.shipping_fee || null,
      extra_description_list: data?.product.extra_description_list || [],
      description: data?.product.description || null,
      badge: {
        above_atf_title_badge_list: data?.product.badge.above_atf_title_badge_list ?? [],
        below_atf_price_badge_list: data?.product.badge.below_atf_price_badge_list ?? [],
      },
      reliability: data?.product.reliability || null,
      shipping_company: data?.product.shipping_company || null,
      beauty_info: data?.product.beauty_info || null,
      browsing_type: data?.product.browsing_type as PdpBrowsingType,
      description_top_banner: data?.product.description_top_banner || null,
      cross_seller_product_count: data?.product.cross_seller_product_count || 0,
      first_order_benefit_banner: data?.product.first_order_benefit_banner || null,
      oversea_direct_banner_image_url: data?.product.oversea_direct_banner_image_url || null,
      product_info_map: data?.product.product_info_map || null,
      purchase_notice: data?.product.purchase_notice || null,
      store_deal_banner: data?.product.store_deal_banner || null,
      store_deal_guide_banner_url: data?.product.store_deal_guide_banner_url || null,
      vertical_page_banner: data?.product.vertical_page_banner || null,
    },
    shop: {
      id: data?.shop.id || '',
      main_domain: data?.shop.main_domain || '',
      name: data?.shop.name || '',
      url: data?.shop.url || '',
      keywords: data?.shop.keywords || '',
      typical_image_url: data?.shop.typical_image_url || null,
      bookmark_count: data?.shop.bookmark_count || null,
      is_saved_shop: data?.shop.is_saved_shop ?? false,
      trait: {
        category: data?.shop.trait.category || null,
        category_list: data?.shop.trait.category_list || null,
        style: data?.shop.trait.style || null,
        minor_style: data?.shop.trait.minor_style || null,
        minor_category: data?.shop.trait.minor_category || null,
        minor_category_list: data?.shop.trait.minor_category_list || null,
        age10: data?.shop.trait.age10 || null,
        age20early: data?.shop.trait.age20early || null,
        age20mid: data?.shop.trait.age20mid || null,
        age20late: data?.shop.trait.age20late || null,
        age30early: data?.shop.trait.age30early || null,
        age30mid: data?.shop.trait.age30mid || null,
        age30late: data?.shop.trait.age30late || null,
      },
      customer_center_tel: data?.shop.customer_center_tel || null,
      customer_center: data?.shop.customer_center || null,
      main_contact: data?.shop.main_contact || null,
      business_license: data?.shop.business_license || null,
      shop_business_info: data?.shop.shop_business_info || null,
      online_sales_license: data?.shop.online_sales_license || null,
      shipping_address: data?.shop.shipping_address || null,
      return_address: data?.shop.return_address || null,
      type: data?.shop.type || PdpShopType.META,
      zpay_status: data?.shop.zpay_status || null,
      shop_category_list: data?.shop.shop_category_list || [],
      parent_id: data?.shop.parent_id || null,
      sale_type: data?.shop.sale_type || null,
      is_mileage_shop: data?.shop.is_mileage_shop || false,
      coupon_description: data?.shop.coupon_description || null,
      usable_mileage: data?.shop.usable_mileage || null,
    },
    brand_home: data?.brand_home || null,
    seller_notice: data?.seller_notice || null,
    policy_list: data?.policy_list || [],

    recommendation_tab_label: data?.recommendation_tab_label || '',
    flags: data?.flags ?? {
      show_review_summary_best_carousel: false,
      is_matching_option: false,
      is_selected_shop: false,
      is_purchase_only_one_at_time: false,
      is_size_tab_visible: false,
      is_cart_button_visible: false,
      is_family_sale_product: false,
      is_detected_crawler: false,
    },
    key_color: data?.key_color ?? {
      buy_button: {
        text: {
          disabled: '#FA6EE3',
          enabled: '#FA6EE3',
        },
        background: { disabled: '#FFB2F3', enabled: '#FA6EE3' },
      },
      cart_button_of_navigation_bar: '#FA6EE3',
      discount_info_of_atf: '#FA6EE3',
    },
    tab_list: data?.tab_list ?? null,
  };
};

interface Param {
  catalog_product_id: string;
  source?: string;
}

export const PDP_BASE_INFO_QUERY_KEY = ['getPdpBaseInfo'];

export async function prefetchPdpBaseInfo(params: Param, options: GqlRequestOptions, query_client: QueryClient) {
  const query_options = getPdpBaseInfoQueryOption(params, options);
  await query_client.prefetchQuery(query_options);
  const data = await query_client.getQueryData<PdpBaseInfo>(PDP_BASE_INFO_QUERY_KEY);
  return toData(data);
}

export async function requestPdpBaseInfo(param: Param, options?: GqlRequestOptions) {
  try {
    if (!checkValidCatalogProductId(param.catalog_product_id)) {
      throw ERROR_INVALID_PARAMS_CATALOG_PRODUCT_ID;
    }
    const {
      data: { catalog_product_base_info, pdp_btf_page },
    } = await getPdpBaseInfo(param, { ...options, show_alert: false });
    if (!catalog_product_base_info) {
      throw ERROR_FAILED_FETCH;
    }
    return {
      ...catalog_product_base_info,
      tab_list: pdp_btf_page?.tab_list ?? null,
    };
  } catch (e) {
    console.error(e);
  }
}

export function getPdpBaseInfoQueryOption(param: Param, options?: GqlRequestOptions) {
  return {
    queryKey: PDP_BASE_INFO_QUERY_KEY,
    queryFn: () => requestPdpBaseInfo(param, options),
    ...queryOptions,
  };
}

export const usePdpBaseInfo = (param: Param, options?: GqlRequestOptions) => {
  const response = useQuery(getPdpBaseInfoQueryOption(param, options));
  return { ...response, ...toData(response.data) };
};
